import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isEqual } from 'lodash-es';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TabQSParam } from '~pages/Approvals/DriverPay/TabQSParam';

import {
  ALL_INVOICE_STATES,
  APPROVED_INVOICE_STATES,
  APPROVED_TOGGLE_QS_PARAM,
  IN_SETTLEMENT_TOGGLE_QS_PARAM,
  UNAPPROVED_INVOICE_STATES,
  UNAPPROVED_TOGGLE_QS_PARAM,
} from '../../constants';
import DriverPayPageContext from '../../DriverPayPageContext';
import DriverPayQueryContext from '../../DriverPayQueryContext';

interface QuickFilterButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function QuickFilterButton({ label, isActive, onClick }: QuickFilterButtonProps) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        alignItems: 'center',
        bgcolor: isActive
          ? theme.brandV2.colors.treadGray7
          : theme.semantic.surfaceRaised,
        border: `solid 1px ${isActive ? theme.brandV2.colors.treadGray5 : theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        px: 1.2,
        py: 0.5,
        borderRadius: '48px',
        cursor: 'pointer',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function InvoiceStateFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const driverPayQuery = useContext(DriverPayQueryContext);
  const { uncheckAllInvoices } = useContext(DriverPayPageContext);

  const selectedTab = searchParams.get('tab') as TabQSParam | null;
  const isInvoicesTab = selectedTab === TabQSParam.INVOICES || selectedTab === null;

  // Read toggle states from URL params
  const isUnapprovedActive = searchParams.get(UNAPPROVED_TOGGLE_QS_PARAM) === '1';
  const isApprovedActive = searchParams.get(APPROVED_TOGGLE_QS_PARAM) === '1';
  const isInSettlementActive = searchParams.get(IN_SETTLEMENT_TOGGLE_QS_PARAM) === '1';

  // Track whether we've completed initial mount
  const hasInitializedRef = useRef(false);

  // Apply filters when toggle states change (but not on initial mount with default state)
  useEffect(() => {
    if (!isInvoicesTab) {
      return;
    }

    const isDefaultState =
      !isUnapprovedActive && !isApprovedActive && !isInSettlementActive;

    // Skip initial mount when showing default state (all toggles off)
    // DriverPayTabs already handles applying the initial default filters
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (isDefaultState) {
        return;
      }
    }

    let invoiceStates: InvoiceState[];

    if (isUnapprovedActive && isApprovedActive) {
      // Both on - show all
      invoiceStates = ALL_INVOICE_STATES;
    } else if (isUnapprovedActive) {
      // Only unapproved
      invoiceStates = UNAPPROVED_INVOICE_STATES;
    } else if (isApprovedActive) {
      // Only approved
      invoiceStates = APPROVED_INVOICE_STATES;
    } else {
      // Neither - show all
      invoiceStates = ALL_INVOICE_STATES;
    }

    // Determine grouped filter value:
    // - true: "In Settlement" selected alone
    // - undefined: "In Settlement" combined with other filters
    // - false: only approved/unapproved selected (no "In Settlement")
    let grouped: boolean | undefined;
    if (isInSettlementActive && !isUnapprovedActive && !isApprovedActive) {
      grouped = true;
    } else if (isInSettlementActive) {
      grouped = undefined;
    } else if (isUnapprovedActive || isApprovedActive) {
      grouped = false;
    }

    // Skip value-equal re-applies: applyFilters clears the query cache, so a no-op
    // write still costs a refetch. Matters after clearAllFiltersExceptSearch, which
    // already set this exact state before deleting the toggle URL params.
    const contextAlreadyMatches =
      isEqual(driverPayQuery.filters.invoiceStates, invoiceStates) &&
      driverPayQuery.filters.grouped === grouped;

    if (contextAlreadyMatches) {
      return;
    }

    driverPayQuery.applyFilters({
      invoiceStates,
      grouped,
    });
    // Deliberately not depending on driverPayQuery.filters: this sync is one-way
    // (URL toggles → context). Re-running on context changes would overwrite other
    // writers — on first load DriverPayTabs applies the Invoices tab's narrower
    // default, which an all-toggles-off re-run would replace with ALL_INVOICE_STATES.
    // The filters read above only skips value-equal re-applies.
  }, [isUnapprovedActive, isApprovedActive, isInSettlementActive, isInvoicesTab]);

  const handleUnapprovedClick = useCallback(() => {
    uncheckAllInvoices();

    setSearchParams((params) => {
      if (isUnapprovedActive) {
        params.delete(UNAPPROVED_TOGGLE_QS_PARAM);
      } else {
        params.set(UNAPPROVED_TOGGLE_QS_PARAM, '1');
      }
      return params;
    });
  }, [uncheckAllInvoices, isUnapprovedActive, setSearchParams]);

  const handleApprovedClick = useCallback(() => {
    uncheckAllInvoices();

    setSearchParams((params) => {
      if (isApprovedActive) {
        params.delete(APPROVED_TOGGLE_QS_PARAM);
      } else {
        params.set(APPROVED_TOGGLE_QS_PARAM, '1');
      }
      return params;
    });
  }, [uncheckAllInvoices, isApprovedActive, setSearchParams]);

  const handleInSettlementClick = useCallback(() => {
    uncheckAllInvoices();

    setSearchParams((params) => {
      if (isInSettlementActive) {
        params.delete(IN_SETTLEMENT_TOGGLE_QS_PARAM);
      } else {
        params.set(IN_SETTLEMENT_TOGGLE_QS_PARAM, '1');
      }
      return params;
    });
  }, [uncheckAllInvoices, isInSettlementActive, setSearchParams]);

  if (!isInvoicesTab) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <QuickFilterButton
        label={t('approvals.driver_pay.invoice_state_filter.unapproved')}
        isActive={isUnapprovedActive}
        onClick={handleUnapprovedClick}
      />
      <QuickFilterButton
        label={t('approvals.driver_pay.invoice_state_filter.approved')}
        isActive={isApprovedActive}
        onClick={handleApprovedClick}
      />
      <QuickFilterButton
        label={t('approvals.driver_pay.invoice_state_filter.in_settlement')}
        isActive={isInSettlementActive}
        onClick={handleInSettlementClick}
      />
    </Box>
  );
}
